import { useRef, useLayoutEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'

// Base tab item interface
interface BaseTabItem {
  id: string
  label: string
  icon?: React.ElementType
}

// For routing tabs
interface RoutingTabItem extends BaseTabItem {
  path: string
}


// Routing tabs props (uses NavLink)
interface RoutingTabsProps {
  items: RoutingTabItem[]
  variant?: 'default' | 'compact'
  className?: string
}

// Button tabs props (uses callbacks)
interface ButtonTabsProps<T extends string> {
  items: { id: string; label: string; icon?: React.ElementType; value: T }[]
  value: T
  onChange: (value: T) => void
  disabled?: boolean
  variant?: 'default' | 'compact'
  className?: string
}

/**
 * Routing-based tabs using NavLink for navigation
 */
export function RoutingTabs({ items, variant = 'default', className }: RoutingTabsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Map<string, HTMLAnchorElement>>(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })
  const location = useLocation()

  const activeTab = items.find((tab) => location.pathname === tab.path)?.id

  useLayoutEffect(() => {
    if (!activeTab) return

    const rafId = requestAnimationFrame(() => {
      const activeEl = tabRefs.current.get(activeTab)
      const container = containerRef.current
      if (activeEl && container) {
        const containerRect = container.getBoundingClientRect()
        const activeRect = activeEl.getBoundingClientRect()
        setIndicatorStyle({
          left: activeRect.left - containerRect.left,
          width: activeRect.width,
        })
      }
    })

    return () => cancelAnimationFrame(rafId)
  }, [activeTab])

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative inline-flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]',
        className
      )}
    >
      {/* Sliding indicator */}
      <div
        className="absolute top-1 bottom-1 rounded-md bg-white/[0.08] border border-white/[0.1] transition-all duration-300 ease-out pointer-events-none"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
      />
      {items.map((tab) => {
        const Icon = tab.icon
        return (
          <NavLink
            key={tab.id}
            to={tab.path}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.id, el)
              else tabRefs.current.delete(tab.id)
            }}
            className={({ isActive }) =>
              cn(
                'relative z-10 flex items-center gap-2 rounded-md font-medium transition-colors duration-200 whitespace-nowrap',
                variant === 'compact' ? 'px-3 py-1.5 text-xs' : 'px-2 md:px-4 py-2 text-xs md:text-sm',
                isActive ? 'text-white' : 'text-[#666] hover:text-[#999]'
              )
            }
          >
            {Icon && <Icon className={cn('shrink-0', variant === 'compact' ? 'size-4' : 'hidden md:block size-4')} />}
            {tab.label}
          </NavLink>
        )
      })}
    </div>
  )
}

/**
 * Button-based tabs for state management
 */
export function ButtonTabs<T extends string>({
  items,
  value,
  onChange,
  disabled = false,
  variant = 'default',
  className,
}: ButtonTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  useLayoutEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const activeEl = tabRefs.current.get(value)
      const container = containerRef.current
      if (activeEl && container) {
        const containerRect = container.getBoundingClientRect()
        const activeRect = activeEl.getBoundingClientRect()
        setIndicatorStyle({
          left: activeRect.left - containerRect.left,
          width: activeRect.width,
        })
      }
    })

    return () => cancelAnimationFrame(rafId)
  }, [value])

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative inline-flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]',
        className
      )}
    >
      {/* Sliding indicator */}
      <div
        className="absolute top-1 bottom-1 rounded-md bg-white/[0.08] border border-white/[0.1] transition-all duration-300 ease-out pointer-events-none"
        style={{
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }}
      />
      {items.map((tab) => {
        const Icon = tab.icon
        return (
          <button
            key={tab.id}
            type="button"
            ref={(el) => {
              if (el) tabRefs.current.set(tab.value, el)
              else tabRefs.current.delete(tab.value)
            }}
            onClick={() => onChange(tab.value)}
            disabled={disabled}
            className={cn(
              'relative z-10 flex items-center gap-2 rounded-md font-medium transition-colors duration-200 whitespace-nowrap',
              variant === 'compact' ? 'px-3 py-1.5 text-xs' : 'px-3 py-1.5 text-sm',
              value === tab.value ? 'text-white' : 'text-[#666] hover:text-[#999]',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {Icon && <Icon className="size-4 shrink-0" />}
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
